<template>
	<section id="super-admin-analytics">
		<!-- Date range filter -->
		<div class="row justify-content-end mb-2">
			<b-col md="4">
				<b-form-datepicker placeholder="Choose a date" local="en" v-model="from_date" @input="reload" />
			</b-col>
			<div md="1" class="d-flex align-items-center ml-1">to</div>
			<b-col md="4">
				<b-form-datepicker placeholder="Choose a date" local="en" v-model="to_date" @input="reload" />
			</b-col>
			<b-col md="auto" class="d-flex align-items-center">
				<b-button v-if="from_date || to_date" variant="outline-secondary" size="sm" @click="clearDates">
					Clear
				</b-button>
			</b-col>
		</div>

		<!-- KPI cards -->
		<b-row class="match-height mb-1">
			<b-col md="3" sm="6">
				<b-card no-body class="kpi-card">
					<span class="b-avatar badge-light-primary rounded-circle kpi-icon"
						><feather-icon icon="ThermometerIcon" size="20"
					/></span>
					<div>
						<h4 class="mb-0 font-weight-bolder">{{ formatNumber(totals.broiler_birds) }}</h4>
						<small class="text-muted">Broiler Birds</small>
					</div>
				</b-card>
			</b-col>
			<b-col md="3" sm="6">
				<b-card no-body class="kpi-card">
					<span class="b-avatar badge-light-info rounded-circle kpi-icon"
						><feather-icon icon="FeatherIcon" size="20"
					/></span>
					<div>
						<h4 class="mb-0 font-weight-bolder">{{ formatNumber(totals.breeder_birds) }}</h4>
						<small class="text-muted">Breeder Birds</small>
					</div>
				</b-card>
			</b-col>
			<b-col md="3" sm="6">
				<b-card no-body class="kpi-card">
					<span class="b-avatar badge-light-success rounded-circle kpi-icon"
						><feather-icon icon="CircleIcon" size="20"
					/></span>
					<div>
						<h4 class="mb-0 font-weight-bolder">{{ formatNumber(totals.layer_birds) }}</h4>
						<small class="text-muted">Layer Birds</small>
					</div>
				</b-card>
			</b-col>
			<b-col md="3" sm="6">
				<b-card no-body class="kpi-card">
					<span class="b-avatar badge-light-warning rounded-circle kpi-icon"
						><feather-icon icon="ClockIcon" size="20"
					/></span>
					<div>
						<h4 class="mb-0 font-weight-bolder">{{ formatNumber(maturingTotalBirds) }}</h4>
						<small class="text-muted">Broilers in 30–45 Day Window</small>
					</div>
				</b-card>
			</b-col>
		</b-row>

		<!-- State-wise + Country-wise charts -->
		<b-row class="match-height mb-1">
			<b-col lg="8">
				<b-card no-body class="p-2">
					<div class="d-flex justify-content-between align-items-center mb-1">
						<h5 class="mb-0">State-wise Bird Count by Production Type</h5>
					</div>
					<vue-apex-charts
						v-if="stateChart.series[0].data.length"
						type="bar"
						height="360"
						:options="stateChart.options"
						:series="stateChart.series"
					/>
					<p v-else class="text-muted text-center py-5 mb-0">No state-wise flock data yet.</p>
				</b-card>
			</b-col>
			<b-col lg="4">
				<b-card no-body class="p-2 h-100">
					<h5 class="mb-1">Country-wise Bird Share</h5>
					<vue-apex-charts
						v-if="countryChart.series.length"
						type="donut"
						height="320"
						:options="countryChart.options"
						:series="countryChart.series"
					/>
					<p v-else class="text-muted text-center py-5 mb-0">No country-wise flock data yet.</p>
				</b-card>
			</b-col>
		</b-row>

		<!-- Maturing 30-45 days -->
		<b-row class="match-height mb-1">
			<b-col lg="6">
				<b-card no-body class="p-2">
					<h5 class="mb-1">Broilers Nearing Sale (30–45 Days) — State-wise</h5>
					<b-table
						small
						:items="analytics.maturing_30_45_days.state_wise"
						:fields="maturingStateFields"
						show-empty
						empty-text="No broiler flocks currently in the 30-45 day window."
					/>
				</b-card>
			</b-col>
			<b-col lg="6">
				<b-card no-body class="p-2">
					<h5 class="mb-1">Broilers Nearing Sale (30–45 Days) — Country-wise</h5>
					<b-table
						small
						:items="analytics.maturing_30_45_days.country_wise"
						:fields="maturingCountryFields"
						show-empty
						empty-text="No broiler flocks currently in the 30-45 day window."
					/>
				</b-card>
			</b-col>
		</b-row>

		<!-- Company-wise last data entry -->
		<b-row class="match-height">
			<b-col cols="12">
				<b-card no-body class="p-2">
					<div class="d-flex justify-content-between align-items-center mb-1">
						<h5 class="mb-0">Company-wise Last Data Entry</h5>
						<b-form-input
							v-model="companySearch"
							placeholder="Search company..."
							size="sm"
							style="max-width: 220px"
						/>
					</div>
					<b-table
						small
						:items="filteredCompanies"
						:fields="companyFields"
						show-empty
						empty-text="No companies found."
					>
						<template #cell(company_name)="{ item }">
							<span class="font-weight-bold">{{ item.company_name }}</span>
						</template>
						<template #cell(last_activity_at)="{ item }">
							<span v-if="item.last_activity_at">{{ item.last_activity_at }}</span>
							<span v-else class="text-muted">Never</span>
						</template>
						<template #cell(days_since_activity)="{ item }">
							<b-badge :variant="staleVariant(item)" pill>
								{{ item.days_since_activity === null ? "No data" : `${item.days_since_activity}d ago` }}
							</b-badge>
						</template>
						<template #row-details="{ item }">
							<div class="pl-3 pr-3 pb-2">
								<b-badge
									v-for="f in item.by_field"
									:key="f.field"
									variant="light-secondary"
									class="mr-1 mb-1"
								>
									{{ f.field }}: {{ f.last_entry_at || "—" }}
								</b-badge>
							</div>
						</template>
						<template #cell(expand)="{ item, toggleDetails }">
							<b-link @click="toggleDetails">
								<feather-icon :icon="item._showDetails ? 'ChevronUpIcon' : 'ChevronDownIcon'" />
							</b-link>
						</template>
					</b-table>
				</b-card>
			</b-col>
		</b-row>
	</section>
</template>

<script>
import { BRow, BCol, BCard, BTable, BBadge, BFormInput, BFormDatepicker, BButton, BLink } from "bootstrap-vue";
import VueApexCharts from "vue-apexcharts";
import axiosIns from "@/libs/axios";

export default {
	components: { BRow, BCol, BCard, BTable, BBadge, BFormInput, BFormDatepicker, BButton, BLink, VueApexCharts },
	data() {
		return {
			from_date: null,
			to_date: null,
			analytics: {
				state_wise: [],
				country_wise: [],
				maturing_30_45_days: { state_wise: [], country_wise: [] },
			},
			companies: [],
			companySearch: "",
			maturingStateFields: [
				{ key: "state_name", label: "State" },
				{ key: "country_name", label: "Country" },
				{ key: "flock_count", label: "Flocks", sortable: true },
				{ key: "bird_count", label: "Birds", sortable: true },
			],
			maturingCountryFields: [
				{ key: "country_name", label: "Country" },
				{ key: "flock_count", label: "Flocks", sortable: true },
				{ key: "bird_count", label: "Birds", sortable: true },
			],
			companyFields: [
				{ key: "expand", label: "" },
				{ key: "company_name", label: "Company" },
				{ key: "last_activity_at", label: "Last Activity" },
				{ key: "days_since_activity", label: "Status", sortable: true },
			],
		};
	},
	computed: {
		totals() {
			const acc = { broiler_birds: 0, breeder_birds: 0, layer_birds: 0 };
			this.analytics.state_wise.forEach((row) => {
				acc.broiler_birds += Number(row.broiler_birds || 0);
				acc.breeder_birds += Number(row.breeder_birds || 0);
				acc.layer_birds += Number(row.layer_birds || 0);
			});
			return acc;
		},
		maturingTotalBirds() {
			return this.analytics.maturing_30_45_days.country_wise.reduce(
				(sum, row) => sum + Number(row.bird_count || 0),
				0
			);
		},
		filteredCompanies() {
			const q = this.companySearch.trim().toLowerCase();
			if (!q) return this.companies;
			return this.companies.filter((c) => c.company_name?.toLowerCase().includes(q));
		},
		stateChart() {
			// Top 12 states by broiler bird count, keeps the chart readable
			const rows = [...this.analytics.state_wise]
				.sort((a, b) => Number(b.broiler_birds) - Number(a.broiler_birds))
				.slice(0, 12);
			return {
				series: [
					{ name: "Broiler", data: rows.map((r) => Number(r.broiler_birds || 0)) },
					{ name: "Breeder", data: rows.map((r) => Number(r.breeder_birds || 0)) },
					{ name: "Layer", data: rows.map((r) => Number(r.layer_birds || 0)) },
				],
				options: {
					chart: { stacked: false, toolbar: { show: false } },
					plotOptions: { bar: { columnWidth: "55%", borderRadius: 3 } },
					colors: ["#7367f0", "#00cfe8", "#28c76f"],
					xaxis: {
						categories: rows.map((r) => r.state_name || "Unknown"),
						labels: {
							// ApexCharts' own pixel-width truncation cuts labels off
							// mid-word ("...alities, and Peoples' Region") — truncate
							// to a clean character count with an ellipsis instead.
							// The full name still shows in the hover tooltip.
							formatter: (value) =>
								value && value.length > 14 ? `${value.slice(0, 13)}…` : value,
						},
					},
					legend: { position: "top" },
					dataLabels: { enabled: false },
				},
			};
		},
		countryChart() {
			const rows = this.analytics.country_wise.filter((r) => r.country_name);
			const totalPerCountry = rows.map(
				(r) => Number(r.broiler_birds || 0) + Number(r.breeder_birds || 0) + Number(r.layer_birds || 0)
			);
			return {
				series: totalPerCountry,
				options: {
					labels: rows.map((r) => r.country_name),
					legend: { position: "bottom" },
					colors: ["#7367f0", "#00cfe8", "#28c76f", "#ff9f43", "#ea5455"],
				},
			};
		},
	},
	created() {
		this.reload();
	},
	methods: {
		formatNumber(n) {
			return new Intl.NumberFormat("en-IN").format(n || 0);
		},
		staleVariant(item) {
			if (item.days_since_activity === null) return "secondary";
			if (item.days_since_activity <= 2) return "success";
			if (item.days_since_activity <= 7) return "warning";
			return "danger";
		},
		dateParams() {
			const params = {};
			if (this.from_date) params.from_date = this.from_date;
			if (this.to_date) params.to_date = this.to_date;
			return params;
		},
		reload() {
			this.loadAnalytics();
			this.loadLastDataEntry();
		},
		clearDates() {
			this.from_date = null;
			this.to_date = null;
			this.reload();
		},
		loadAnalytics() {
			axiosIns
				.get("web/super-admin/farm-analytics", { params: this.dateParams() })
				.then((res) => {
					this.analytics = res.data?.data ?? this.analytics;
				})
				.catch(() => {
					this.$bvToast?.toast("Failed to load farm analytics", { variant: "danger", title: "Error" });
				});
		},
		loadLastDataEntry() {
			axiosIns
				.get("web/super-admin/last-data-entry", { params: this.dateParams() })
				.then((res) => {
					this.companies = res.data?.data ?? [];
				})
				.catch(() => {
					this.$bvToast?.toast("Failed to load last data entry report", { variant: "danger", title: "Error" });
				});
		},
	},
};
</script>

<style lang="scss" scoped>
.kpi-card {
	padding: 1.1rem;
	flex-direction: row;
	align-items: center;
	gap: 0.9rem;

	.kpi-icon {
		width: 44px;
		height: 44px;
		flex-shrink: 0;
		display: flex;
		align-items: center;
		justify-content: center;
	}
}
</style>
